/**
 * A caixa "Conferência — Importar Boleto (PDF)", com uma linha por boleto lido.
 *
 * Papel: mostra favorecido, descrição, categoria e subcategoria, valor e
 * vencimento de cada boleto, todos editáveis; os avisos da leitura em destaque;
 * o aviso de duplicidade com os lançamentos já carregados na aba; e a
 * residência, que é obrigatória porque o boleto não a diz. "Confirmar e Criar
 * Lançamentos" grava cada boleto como lançamento pendente de despesa, pela
 * `validarModal` e pelo `useSalvarLancamento` do modal de lançamento.
 *
 * Como usar: quem monta passa os boletos já lidos, cada um com o nome do arquivo;
 * o plano de contas; e os lançamentos carregados na aba. A duplicidade só enxerga
 * esses lançamentos, e não consulta a casa inteira. Um campo que a leitura não
 * trouxe chega vazio, e a validação do lançamento recusa a gravação com a
 * mensagem dela. Se a gravação parar no meio, os boletos já gravados saem da
 * caixa e a caixa continua aberta com os restantes, para que nada seja criado
 * duas vezes.
 */
import {
  type Categoria,
  type LancamentoDaListagem,
  type SalvarLancamento,
} from '@concierge/shared';
import { useRef, useState } from 'react';

import { casasQueRecebemRegistro, useResidencias } from '@/modules/cadastros-base';
import { useNotificacao } from '@/shared/embutido';
import { lerValorDigitado, mascararCentavos } from '@/shared/lib/mascara';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  type ValoresDoLancamento,
  categoriasDoTipo,
  subcategoriasDoTipo,
  validarModal,
  valoresDoNovo,
} from '../formulario-do-lancamento';
import { useSalvarLancamento } from '../hooks/usar-financas';
import { type BoletoParaConferencia } from '../importacao/provedores-de-boleto';
import { AvisoDeConferencia } from './kit/aviso-de-conferencia';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import { CLASSE_DO_CONTROLE, CLASSE_DO_SELECT, Campo, LinhaDeCampos } from './kit/campo';

/** Um boleto lido, com o nome do arquivo de onde saiu. */
export interface ItemDoBoleto {
  id: string;
  arquivo: string;
  boleto: BoletoParaConferencia;
}

interface ConferenciaDeBoletosProps {
  /** Os boletos lidos, na ordem em que os arquivos foram escolhidos. */
  itens: readonly ItemDoBoleto[];
  /** Os arquivos que não foram lidos, com o aviso de cada um. */
  avisosDeArquivo: readonly string[];
  /** O plano de contas, para as categorias e subcategorias de despesa. */
  plano: readonly Categoria[];
  /** Os lançamentos já carregados na aba, para o aviso de duplicidade. */
  lancamentos: readonly LancamentoDaListagem[];
  onFechar: () => void;
}

/** Uma linha da conferência, com os campos como estão no formulário. */
interface LinhaDoBoleto {
  id: string;
  arquivo: string;
  favorecido: string;
  descricao: string;
  categoria: string;
  subcategoria: string;
  /** O valor mascarado em centavos, como o campo do modal. */
  valor: string;
  /** `AAAA-MM-DD`, ou vazio quando a leitura não trouxe o vencimento. */
  vencimento: string;
  observacao: string;
  avisos: readonly string[];
}

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * A linha de conferência de um boleto lido.
 *
 * @param item - O boleto e o arquivo de onde saiu.
 * @returns A linha com cada campo preenchido só com o que a leitura trouxe.
 */
function linhaDoItem(item: ItemDoBoleto): LinhaDoBoleto {
  const { boleto } = item;
  return {
    id: item.id,
    arquivo: item.arquivo,
    favorecido: boleto.favorecidoNome ?? '',
    descricao: boleto.descricao,
    categoria: boleto.categoria ?? '',
    subcategoria: boleto.subcategoria ?? '',
    valor:
      boleto.valor === null ? '' : mascararCentavos(String(Math.round(boleto.valor * 100))),
    vencimento: boleto.vencimento ?? '',
    observacao: boleto.observacao ?? '',
    avisos: boleto.avisos,
  };
}

/**
 * Os valores do formulário de lançamento para uma linha de boleto.
 *
 * @param linha - A linha como está na caixa.
 * @param plano - O plano de contas, para o padrão dos campos que não são do boleto.
 * @returns Os valores que `validarModal` recebe, com forma boleto e pago falso.
 */
function valoresDaLinha(
  linha: LinhaDoBoleto,
  plano: readonly Categoria[],
): ValoresDoLancamento {
  return {
    ...valoresDoNovo(hojeEmIso(), plano),
    favorecido: linha.favorecido,
    data: linha.vencimento,
    categoria: linha.categoria,
    subcategoria: linha.subcategoria,
    descricao: linha.descricao,
    formaPagamento: 'boleto',
    valor: linha.valor,
    pago: false,
    observacao: linha.observacao,
  };
}

/**
 * Se um lançamento carregado parece o mesmo boleto: valor, vencimento e favorecido.
 *
 * @param linha - A linha de boleto.
 * @param lancamento - Um lançamento da aba.
 * @returns Verdadeiro quando os três campos batem; o favorecido precisa existir.
 */
function eDuplicadoDe(linha: LinhaDoBoleto, lancamento: LancamentoDaListagem): boolean {
  const favorecido = linha.favorecido.trim().toLocaleLowerCase('pt-BR');
  const favorecidoDoLancamento = (lancamento.favorecido?.nome ?? '')
    .trim()
    .toLocaleLowerCase('pt-BR');
  return (
    favorecido !== '' &&
    favorecidoDoLancamento === favorecido &&
    lancamento.data === linha.vencimento &&
    Math.abs(lancamento.valorBruto - lerValorDigitado(linha.valor)) < 0.005
  );
}

/**
 * A conferência dos boletos lidos.
 *
 * @param itens - Os boletos lidos, com o arquivo de cada um.
 * @param avisosDeArquivo - Os arquivos que não foram lidos.
 * @param plano - O plano de contas.
 * @param lancamentos - Os lançamentos carregados na aba, para a duplicidade.
 * @param onFechar - Fecha a caixa: o "✕", o véu, `Esc`, "Cancelar" e a gravação
 *   que deu certo.
 * @returns A caixa de 720 px, com uma parte por boleto.
 */
export function ConferenciaDeBoletos({
  itens,
  avisosDeArquivo,
  plano,
  lancamentos,
  onFechar,
}: ConferenciaDeBoletosProps) {
  const notificar = useNotificacao();
  const salvar = useSalvarLancamento();
  const residencias = useResidencias();
  const gravando = useRef(false);
  const [residenciaId, setResidenciaId] = useState('');
  const [linhas, setLinhas] = useState<LinhaDoBoleto[]>(() => itens.map(linhaDoItem));

  const opcoesDeResidencia =
    residencias.data === undefined ? [] : casasQueRecebemRegistro(residencias.data, null);

  /**
   * Troca um campo de uma linha. Trocar a categoria zera a subcategoria, que
   * pertence a ela.
   *
   * @param id - A linha alterada.
   * @param mudanca - Os campos que mudaram.
   */
  function handleMudarLinha(id: string, mudanca: Partial<LinhaDoBoleto>) {
    setLinhas((anteriores) =>
      anteriores.map((linha) => {
        if (linha.id !== id) return linha;
        const categoriaMudou =
          mudanca.categoria !== undefined && mudanca.categoria !== linha.categoria;
        return {
          ...linha,
          ...mudanca,
          subcategoria: categoriaMudou ? '' : (mudanca.subcategoria ?? linha.subcategoria),
        };
      }),
    );
  }

  /** "Confirmar e Criar Lançamentos": valida tudo, e só então grava um por um. */
  async function handleConfirmar() {
    if (gravando.current) return;
    if (residenciaId === '') {
      notificar('Escolha a residência do boleto.', false);
      return;
    }
    const validados: { id: string; corpo: SalvarLancamento }[] = [];
    for (const linha of linhas) {
      const resultado = validarModal(valoresDaLinha(linha, plano), residenciaId);
      if (!resultado.valido) {
        notificar(`${linha.arquivo}: ${resultado.mensagem}`, false);
        return;
      }
      validados.push({ id: linha.id, corpo: resultado.corpo });
    }
    gravando.current = true;
    const gravados = new Set<string>();
    try {
      for (const { id, corpo } of validados) {
        await salvar.mutateAsync({ id: null, corpo });
        gravados.add(id);
      }
      onFechar();
      notificar(
        `✓ ${String(gravados.size)} lançamento${gravados.size > 1 ? 's' : ''} de boleto criado${gravados.size > 1 ? 's' : ''}`,
      );
    } catch (erro) {
      setLinhas((anteriores) => anteriores.filter((linha) => !gravados.has(linha.id)));
      const antes =
        gravados.size > 0
          ? `${String(gravados.size)} gravado${gravados.size > 1 ? 's' : ''} antes da falha. `
          : '';
      notificar(`${antes}${mensagemDe(erro)}`, false);
    } finally {
      gravando.current = false;
    }
  }

  return (
    <CaixaDeDialogo
      titulo="Conferência — Importar Boleto (PDF)"
      largura={720}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void handleConfirmar()} disabled={salvar.isPending}>
            Confirmar e Criar Lançamentos
          </Botao>
        </>
      }
    >
      {avisosDeArquivo.map((aviso) => (
        <AvisoDeConferencia key={aviso}>{aviso}</AvisoDeConferencia>
      ))}
      <Campo rotulo="Residência do boleto" id="boleto-residencia">
        <select
          id="boleto-residencia"
          value={residenciaId}
          onChange={(evento) => setResidenciaId(evento.target.value)}
          disabled={residencias.data === undefined}
          className={CLASSE_DO_SELECT}
        >
          <option value="">
            {residencias.isError
              ? 'Não foi possível carregar as residências'
              : residencias.data === undefined
                ? 'Carregando residências…'
                : '— Escolha —'}
          </option>
          {opcoesDeResidencia.map((casa) => (
            <option key={casa.id} value={casa.id}>
              {casa.nome}
            </option>
          ))}
        </select>
      </Campo>
      <div className="mb-2.5 text-[11px] text-texto-fraco">
        Cada boleto vira um lançamento pendente de despesa, com o vencimento como data e a
        forma boleto. Confira os campos antes de confirmar.
      </div>
      {linhas.map((linha) => {
        const duplicado = lancamentos.some((lancamento) => eDuplicadoDe(linha, lancamento));
        const categorias = categoriasDoTipo(plano, 'despesa');
        const opcoesDeCategoria =
          linha.categoria === '' || categorias.includes(linha.categoria)
            ? categorias
            : [...categorias, linha.categoria];
        const subcategorias = subcategoriasDoTipo(plano, 'despesa', linha.categoria);
        const opcoesDeSubcategoria =
          linha.subcategoria === '' || subcategorias.includes(linha.subcategoria)
            ? subcategorias
            : [...subcategorias, linha.subcategoria];
        return (
          <fieldset key={linha.id} className="mb-3 border-t border-emb-borda pt-2.5">
            <legend className="px-1 text-[12px] font-semibold text-foreground">
              {linha.arquivo}
            </legend>
            {linha.avisos.map((aviso) => (
              <AvisoDeConferencia key={aviso}>{aviso}</AvisoDeConferencia>
            ))}
            {duplicado && (
              <AvisoDeConferencia>
                Já existe lançamento com mesmo valor, vencimento e favorecido. Confira antes de
                gravar; o aviso não bloqueia a gravação.
              </AvisoDeConferencia>
            )}
            <LinhaDeCampos colunas={2} className="mb-1.5">
              <Campo rotulo="Favorecido" id={`boleto-${linha.id}-favorecido`}>
                <input
                  id={`boleto-${linha.id}-favorecido`}
                  type="text"
                  value={linha.favorecido}
                  onChange={(evento) =>
                    handleMudarLinha(linha.id, { favorecido: evento.target.value })
                  }
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
              <Campo rotulo="Descrição" id={`boleto-${linha.id}-descricao`}>
                <input
                  id={`boleto-${linha.id}-descricao`}
                  type="text"
                  value={linha.descricao}
                  onChange={(evento) =>
                    handleMudarLinha(linha.id, { descricao: evento.target.value })
                  }
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
            </LinhaDeCampos>
            <LinhaDeCampos colunas={2} className="mb-1.5">
              <Campo rotulo="Categoria" id={`boleto-${linha.id}-categoria`}>
                <select
                  id={`boleto-${linha.id}-categoria`}
                  value={linha.categoria}
                  onChange={(evento) =>
                    handleMudarLinha(linha.id, { categoria: evento.target.value })
                  }
                  className={CLASSE_DO_SELECT}
                >
                  <option value="">— Selecionar —</option>
                  {opcoesDeCategoria.map((nome) => (
                    <option key={nome} value={nome}>
                      {nome}
                    </option>
                  ))}
                </select>
              </Campo>
              <Campo rotulo="Subcategoria" id={`boleto-${linha.id}-subcategoria`}>
                <select
                  id={`boleto-${linha.id}-subcategoria`}
                  value={linha.subcategoria}
                  onChange={(evento) =>
                    handleMudarLinha(linha.id, { subcategoria: evento.target.value })
                  }
                  className={CLASSE_DO_SELECT}
                >
                  <option value="">— Selecionar —</option>
                  {opcoesDeSubcategoria.map((nome) => (
                    <option key={nome} value={nome}>
                      {nome}
                    </option>
                  ))}
                </select>
              </Campo>
            </LinhaDeCampos>
            <LinhaDeCampos colunas={2} className="mb-1.5">
              <Campo rotulo="Valor (R$)" id={`boleto-${linha.id}-valor`}>
                <input
                  id={`boleto-${linha.id}-valor`}
                  type="text"
                  inputMode="numeric"
                  value={linha.valor}
                  onChange={(evento) =>
                    handleMudarLinha(linha.id, {
                      valor: mascararCentavos(evento.target.value),
                    })
                  }
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
              <Campo rotulo="Vencimento" id={`boleto-${linha.id}-vencimento`}>
                <input
                  id={`boleto-${linha.id}-vencimento`}
                  type="date"
                  value={linha.vencimento}
                  onChange={(evento) =>
                    handleMudarLinha(linha.id, { vencimento: evento.target.value })
                  }
                  className={CLASSE_DO_CONTROLE}
                />
              </Campo>
            </LinhaDeCampos>
          </fieldset>
        );
      })}
    </CaixaDeDialogo>
  );
}
